import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
/**
 * Content Inspection Page
 *
 * Layout: Company /review-contents 참고
 * - 좌측 사이드바: 커리큘럼 계층 트리 (accordion)
 * - 우측 컨텐츠: 영상/문제/문제 커버리지 탭 (내용 기반 카드 UI)
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  Layers,
  BrainCircuit,
  BookOpenCheck,
  BookOpen,
  FolderOpen,
  PlayCircle,
  Video,
  FileQuestion,
  LayoutGrid,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { adminApi } from '@/api/admin'
import type { ContentHierarchyNode, ContentHierarchyResponse } from '@/api/admin'
import { getQuizGroupAssociations } from '@/api/quizGroupAssociation'
import { AiqModuleInspection, VideoPanel, QuizPanel, QuizCoveragePanel } from '@/components/inspection'
import { findHierarchyNode, parseInspectionId } from '@/components/inspection/aiqModuleInspectionModel'
import {
  createInspectionLessonPath,
  getNextNodeOpenState,
  isSameInspectionLessonPath,
  type InspectionLessonPath,
} from './contentInspectionTreeState'

// ── Types ─────────────────────────────────────────────────────────────────────

type ReviewMode = 'video' | 'quiz' | 'coverage'
type InspectionMode = 'lesson' | 'aiq'

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'curriculum': return Layers
    case 'course': return BookOpen
    case 'module': return FolderOpen
    case 'lesson': return PlayCircle
    default: return FolderOpen
  }
}

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

function InspectionSidebar({
  selectionMode,
  currentCurriculumId,
  currentLessonPath,
  currentModuleId,
  onLessonSelect,
  onModuleSelect,
  onCurriculumSelect,
  onHierarchyChange,
}: {
  selectionMode: InspectionMode
  currentCurriculumId: number | null
  currentLessonPath?: InspectionLessonPath | null
  currentModuleId?: number
  onLessonSelect: (path: InspectionLessonPath) => void
  onModuleSelect: (moduleId: number) => void
  onCurriculumSelect: (curriculumId: number | null) => void
  onHierarchyChange: (hierarchy: ContentHierarchyResponse | undefined) => void
}) {
  const [openNodes, setOpenNodes] = useState<Record<string, boolean>>({})

  const { data: curricula, isLoading } = useQuery({
    queryKey: ['inspection', 'curricula'],
    queryFn: () => adminApi.getContentsByLevel('curriculum'),
    staleTime: 10 * 60 * 1000,
  })

  const { data: hierarchy } = useQuery({
    queryKey: ['inspection', 'hierarchy', currentCurriculumId],
    queryFn: () => adminApi.getContentHierarchy(currentCurriculumId!, true),
    enabled: !!currentCurriculumId,
    staleTime: 10 * 60 * 1000,
  })

  useEffect(() => {
    onHierarchyChange(hierarchy)
  }, [hierarchy, onHierarchyChange])

  const toggleNode = (nodeId: number) => {
    setOpenNodes(prev => ({
      ...prev,
      [nodeId]: getNextNodeOpenState(prev[nodeId]),
    }))
  }

  const isOpen = (nodeId: number) => openNodes[nodeId] !== false

  const renderNode = (
    node: ContentHierarchyNode,
    depth: number = 0,
    ancestorIds: number[] = []
  ) => {
    const isLesson = node.type === 'lesson'
    const isModule = node.type === 'module'
    const lessonPath = isLesson
      ? createInspectionLessonPath(ancestorIds, node.id)
      : null
    const hasChildren = (node.children?.length ?? 0) > 0
    const nodeOpen = isOpen(node.id)
    const isSelected = selectionMode === 'lesson'
      ? isSameInspectionLessonPath(lessonPath, currentLessonPath)
      : isModule && node.id === currentModuleId
    const Icon = getTypeIcon(node.type)

    if (isLesson) {
      if (selectionMode === 'aiq') {
        return (
          <div
            key={node.id}
            className="flex w-full items-start gap-2.5 px-3 py-2 text-left text-slate-500"
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
            <span className="text-sm font-medium"><ContentMetadataText text={node.bareTitle || node.title} /></span>
          </div>
        )
      }

      return (
        <button
          key={node.id}
          onClick={() => {
            if (lessonPath) onLessonSelect(lessonPath)
          }}
          className={cn(
            'group flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors relative',
            isSelected
              ? 'bg-amber-50 text-amber-900'
              : 'text-slate-600 hover:bg-slate-50'
          )}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          {isSelected && <div className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500" />}
          <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', isSelected ? 'text-amber-600' : 'text-slate-400')} />
          <span className={cn('text-sm', isSelected ? 'font-bold text-amber-700' : 'font-medium')}>
            <ContentMetadataText text={node.bareTitle || node.title} />
          </span>
        </button>
      )
    }

    return (
      <div key={node.id}>
        <div
          className={cn(
            'flex w-full items-center transition-colors',
            isSelected ? 'bg-amber-50' : 'hover:bg-slate-50',
          )}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleNode(node.id)}
              aria-expanded={nodeOpen}
              aria-label={nodeOpen ? `${node.title} 접기` : `${node.title} 펼치기`}
              className="shrink-0 p-2 text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500"
            >
              {nodeOpen
                ? <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
                : <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />}
            </button>
          ) : (
            <span aria-hidden="true" className="w-[30px] shrink-0" />
          )}
          <button
            type="button"
            onClick={() => {
              if (selectionMode === 'aiq' && isModule) onModuleSelect(node.id)
              else toggleNode(node.id)
            }}
            aria-pressed={selectionMode === 'aiq' && isModule ? isSelected : undefined}
            className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500"
          >
            <Icon className={cn('h-4 w-4 shrink-0', node.type === 'course' ? 'text-blue-500' : 'text-amber-500')} />
            <span className={cn('truncate text-sm font-semibold', isSelected ? 'text-amber-800' : 'text-slate-800')}>
              <ContentMetadataText text={node.bareTitle || node.title} />
            </span>
          </button>
        </div>
        {nodeOpen && hasChildren && (
          <div>
            {(node.children ?? [])
              .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
              .map(child => renderNode(
                child,
                depth + 1,
                [...ancestorIds, node.id]
              ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col w-80 h-full border-r shrink-0 border-slate-200 bg-slate-50">
      {/* Header */}
      <div className="px-4 py-4 border-b border-slate-200">
        <h2 className="text-base font-semibold text-slate-900">콘텐츠 검수</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          {selectionMode === 'aiq' ? 'Module을 선택하세요' : '레슨을 선택하세요'}
        </p>
      </div>

      {/* Curriculum Selector */}
      <div className="px-4 py-3 border-b border-slate-200">
        <select
          aria-label="커리큘럼 선택"
          value={currentCurriculumId ?? ''}
          onChange={e => {
            const id = e.target.value ? Number(e.target.value) : null
            onCurriculumSelect(id)
            setOpenNodes({})
          }}
          className="px-3 py-2 w-full text-sm bg-white rounded-lg border border-slate-300 text-slate-700 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
        >
          <option value="">커리큘럼 선택</option>
          {(curricula ?? []).map(c => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
      </div>

      {/* Tree */}
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : !hierarchy ? (
          <div className="px-4 py-8 text-sm text-center text-slate-400">
            커리큘럼을 선택하면 계층 구조가 표시됩니다
          </div>
        ) : (
          <div className="py-1">
            {(hierarchy.children ?? [])
              .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
              .map(child => renderNode(child, 0, [hierarchy.id]))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ContentInspection() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as ReviewMode | null
  const inspectionMode: InspectionMode = searchParams.get('mode') === 'aiq' ? 'aiq' : 'lesson'
  const selectedCurriculumId = parseInspectionId(searchParams.get('curriculumId'))
  const selectedCourseId = parseInspectionId(searchParams.get('courseId'))
  const selectedModuleId = parseInspectionId(searchParams.get('moduleId'))
  const selectedLessonId = parseInspectionId(searchParams.get('lessonId'))

  const [hierarchy, setHierarchy] = useState<ContentHierarchyResponse>()
  const reviewMode: ReviewMode = tabParam === 'quiz' || tabParam === 'coverage' ? tabParam : 'video'
  const selectedLessonPath = useMemo<InspectionLessonPath | null>(() => {
    if (
      selectedCurriculumId === null ||
      selectedCourseId === null ||
      selectedModuleId === null ||
      selectedLessonId === null
    ) {
      return null
    }
    return {
      curriculumId: selectedCurriculumId,
      courseId: selectedCourseId,
      moduleId: selectedModuleId,
      lessonId: selectedLessonId,
    }
  }, [
    selectedCourseId,
    selectedCurriculumId,
    selectedLessonId,
    selectedModuleId,
  ])

  const setReviewMode = useCallback((mode: ReviewMode) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', mode)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setInspectionMode = useCallback((mode: InspectionMode) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('mode', mode)
      if (mode === 'aiq') {
        next.delete('tab')
        next.delete('courseId')
        next.delete('lessonId')
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleModuleSelect = useCallback((moduleId: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('mode', 'aiq')
      next.set('moduleId', String(moduleId))
      next.delete('tab')
      next.delete('courseId')
      next.delete('lessonId')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleCurriculumSelect = useCallback((curriculumId: number | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (curriculumId) next.set('curriculumId', String(curriculumId))
      else next.delete('curriculumId')
      next.delete('courseId')
      next.delete('moduleId')
      next.delete('lessonId')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleHierarchyChange = useCallback((nextHierarchy: ContentHierarchyResponse | undefined) => {
    setHierarchy(nextHierarchy)
  }, [])

  const selectedModule = useMemo(
    () => selectedModuleId ? findHierarchyNode(hierarchy, selectedModuleId, 'module') : null,
    [hierarchy, selectedModuleId],
  )

  const selectedLessonNode = useMemo(
    () => selectedLessonId
      ? findHierarchyNode(hierarchy, selectedLessonId, 'lesson')
      : null,
    [hierarchy, selectedLessonId],
  )

  const associationsQuery = useQuery({
    queryKey: ['inspection', 'aiq-associations', 'global_aiq', true],
    queryFn: () => getQuizGroupAssociations('global_aiq', true),
    enabled: inspectionMode === 'aiq',
    staleTime: 5 * 60 * 1000,
  })

  const { data: lessonContent } = useQuery({
    queryKey: ['inspection', 'lesson', selectedLessonId],
    queryFn: () => selectedLessonId
      ? adminApi.getContentById(selectedLessonId)
      : Promise.reject(new Error('Lesson is not selected')),
    enabled: Boolean(selectedLessonId && !selectedLessonNode?.qid),
    staleTime: 5 * 60 * 1000,
  })

  const qid = selectedLessonNode?.qid || lessonContent?.qid || null
  const selectedLessonTitle = selectedLessonNode?.title || lessonContent?.title

  const handleLessonSelect = useCallback((path: InspectionLessonPath) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('mode', 'lesson')
      next.set('curriculumId', String(path.curriculumId))
      next.set('courseId', String(path.courseId))
      next.set('moduleId', String(path.moduleId))
      next.set('lessonId', String(path.lessonId))
      return next
    }, { replace: true })
  }, [setSearchParams])

  return (
    <div className="flex h-[calc(100vh-48px)] bg-slate-50">
      {/* 좌측 사이드바 */}
      <InspectionSidebar
        selectionMode={inspectionMode}
        currentCurriculumId={selectedCurriculumId}
        currentLessonPath={selectedLessonPath}
        currentModuleId={selectedModuleId ?? undefined}
        onLessonSelect={handleLessonSelect}
        onModuleSelect={handleModuleSelect}
        onCurriculumSelect={handleCurriculumSelect}
        onHierarchyChange={handleHierarchyChange}
      />

      {/* 우측 컨텐츠 */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* 헤더 */}
        <header className="flex flex-wrap justify-between items-center gap-3 px-6 py-3 bg-white border-b border-slate-200">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-slate-900">콘텐츠 검수</h1>
            {inspectionMode === 'lesson' && selectedLessonId && selectedLessonTitle && (
              <p className="text-xs text-slate-500">
                <ContentMetadataText text={selectedLessonTitle} />
                {qid && <span className="ml-2 text-slate-400">QID: {qid}</span>}
              </p>
            )}
          </div>

          {/* 모드 토글 */}
          <div className="flex shrink-0 items-center gap-3">
            <div className="flex gap-1 p-1 rounded-lg bg-slate-100" aria-label="검수 범위">
              <button
                type="button"
                onClick={() => setInspectionMode('lesson')}
                aria-pressed={inspectionMode === 'lesson'}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-amber-500',
                  inspectionMode === 'lesson' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-slate-900',
                )}
              >
                <BookOpenCheck className="w-4 h-4" />
                레슨 콘텐츠
              </button>
              <button
                type="button"
                onClick={() => setInspectionMode('aiq')}
                aria-pressed={inspectionMode === 'aiq'}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-amber-500',
                  inspectionMode === 'aiq' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-slate-900',
                )}
              >
                <BrainCircuit className="w-4 h-4" />
                AIQ 문제
              </button>
            </div>

          {inspectionMode === 'lesson' && selectedLessonId && (
            <div className="flex gap-1 p-1 rounded-lg bg-slate-100">
              <button
                onClick={() => setReviewMode('video')}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                  reviewMode === 'video'
                    ? 'bg-white text-amber-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                )}
              >
                <Video className="w-4 h-4" />
                영상
              </button>
              <button
                onClick={() => setReviewMode('quiz')}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                  reviewMode === 'quiz'
                    ? 'bg-white text-amber-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                )}
              >
                <FileQuestion className="w-4 h-4" />
                문제
              </button>
              <button
                onClick={() => setReviewMode('coverage')}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                  reviewMode === 'coverage'
                    ? 'bg-white text-amber-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                )}
              >
                <LayoutGrid className="w-4 h-4" />
                문제 커버리지
              </button>
            </div>
          )}
          </div>
        </header>

        {/* 메인 컨텐츠 */}
        <main className="overflow-y-auto flex-1 bg-slate-50">
          {inspectionMode === 'aiq' ? (
            selectedModule ? (
              <AiqModuleInspection
                key={selectedModule.id}
                module={selectedModule}
                associations={associationsQuery.data ?? []}
                isLoading={associationsQuery.isLoading}
                error={associationsQuery.error}
                onRetry={() => void associationsQuery.refetch()}
              />
            ) : (
              <div className="flex justify-center items-center h-full">
                <div className="text-center">
                  <FolderOpen className="mx-auto w-12 h-12 text-slate-300" />
                  <p className="mt-3 text-sm text-slate-500">Module을 선택하면 AIQ 문제를 검수할 수 있습니다</p>
                </div>
              </div>
            )
          ) : !selectedLessonPath ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center">
                <PlayCircle className="mx-auto w-12 h-12 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">레슨을 선택하면 검수할 수 있습니다</p>
              </div>
            </div>
          ) : reviewMode === 'video' ? (
            qid ? (
              <VideoPanel
                key={`video-${selectedLessonPath.curriculumId}-${selectedLessonPath.courseId}-${selectedLessonPath.moduleId}-${selectedLessonPath.lessonId}`}
                lessonPath={selectedLessonPath}
                qid={qid}
              />
            ) : (
              <div className="flex justify-center items-center h-full">
                <div className="text-center">
                  <AlertCircle className="mx-auto w-10 h-10 text-slate-300" />
                  <p className="mt-3 text-sm text-slate-500">이 레슨에 연결된 깨처(QID)가 없습니다</p>
                </div>
              </div>
            )
          ) : reviewMode === 'coverage' ? (
            <QuizCoveragePanel key={`coverage-${selectedLessonPath.lessonId}`} lessonId={selectedLessonPath.lessonId} />
          ) : (
            <QuizPanel key={`quiz-${selectedLessonPath.lessonId}`} lessonId={selectedLessonPath.lessonId} />
          )}
        </main>
      </div>
    </div>
  )
}
